import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Check, Zap, Crown } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import CurrencySelector from '@/components/CurrencySelector';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Subscriptions = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { formatPrice } = useCurrency();

  const handleSubscribe = async (planType) => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        email: email,
        plan_type: planType,
        origin_url: originUrl
      });

      const { authorization_url } = response.data;

      // Redirect to Paystack checkout
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 95% OFF Banner */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white py-2 px-4">
        <p className="text-center text-sm sm:text-base font-bold animate-pulse">
          🔥 MASSIVE SALE: 95% OFF ALL FORMULAS! Limited Time Only! 🔥
        </p>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <nav className="hidden md:flex gap-8 items-center">
              <Link to="/products" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Products
              </Link>
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Home
              </Link>
              <CurrencySelector />
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="subscriptions-title">
            Unlimited Access to All Formulas
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
            Subscribe and get instant access to our entire library of 1,200+ business formulas and training materials
          </p>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Monthly Plan */}
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 hover:shadow-2xl transition-all relative" data-testid="monthly-plan">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-sky-100 rounded-full p-3">
                <Zap className="h-6 w-6 text-sky-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Monthly Plan
              </h3>
            </div>
            
            <div className="mb-6">
              <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$4.99</span>
              <span className="text-slate-600">/month</span>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="bg-sky-100 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-sky-600" />
                </div>
                <span className="text-slate-700">Access to all 1,200+ formulas</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-sky-100 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-sky-600" />
                </div>
                <span className="text-slate-700">New formulas added monthly</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-sky-100 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-sky-600" />
                </div>
                <span className="text-slate-700">Priority support access</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-sky-100 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-sky-600" />
                </div>
                <span className="text-slate-700">Cancel anytime</span>
              </li>
            </ul>

            <div className="space-y-4">
              <div>
                <Label htmlFor="monthly-email" className="text-slate-700 mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="monthly-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-lg border-slate-200"
                  disabled={loading}
                  data-testid="monthly-email-input"
                />
              </div>
              <Button
                onClick={() => handleSubscribe('monthly')}
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="subscribe-monthly-btn"
              >
                Subscribe Monthly
              </Button>
            </div>
          </div>

          {/* Yearly Plan */}
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 text-white rounded-2xl p-8 relative overflow-hidden shadow-2xl" data-testid="yearly-plan">
            <div className="absolute top-4 right-4">
              <div className="bg-yellow-400 text-slate-900 px-4 py-1 rounded-full text-sm font-bold">
                BEST VALUE
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="bg-white/20 rounded-full p-3">
                <Crown className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Yearly Plan
              </h3>
            </div>
            
            <div className="mb-2">
              <span className="text-5xl font-bold text-yellow-300" style={{ fontFamily: 'JetBrains Mono, monospace' }}>$39.99</span>
              <span className="text-sky-100">/year</span>
            </div>
            <p className="text-sky-100 mb-6">Just $3.33/month - Best Value!</p>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <div className="bg-white/20 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span>Everything in Monthly plan</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-white/20 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span>Save $100 per year</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-white/20 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span>Exclusive yearly member bonuses</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="bg-white/20 rounded-full p-1 mt-1">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <span>Direct line to support team</span>
              </li>
            </ul>

            <div className="space-y-4">
              <div>
                <Label htmlFor="yearly-email" className="text-white mb-2 block">
                  Email Address
                </Label>
                <Input
                  id="yearly-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-lg border-white/20 bg-white/10 text-white placeholder:text-white/60"
                  disabled={loading}
                  data-testid="yearly-email-input"
                />
              </div>
              <Button
                onClick={() => handleSubscribe('yearly')}
                disabled={loading}
                className="w-full bg-white text-sky-600 hover:bg-sky-50 rounded-full h-12 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="subscribe-yearly-btn"
              >
                Subscribe Yearly
              </Button>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-24 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-12" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Why Subscribe?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Massive Savings
              </h3>
              <p className="text-slate-600">
                Individual formulas cost $20-$90 each. Save thousands with unlimited access to our entire library.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Always Updated
              </h3>
              <p className="text-slate-600">
                Get instant access to new formulas and updates as soon as they're released. Stay ahead of trends.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Build Multiple Businesses
              </h3>
              <p className="text-slate-600">
                Explore different industries and product lines without additional costs. Test and scale what works.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
